import { Box, Button, Group } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { NAVY } from './styles';
import { BackButton } from './BackButton';
import { PageNodeRenderer } from './PageNodeRenderer';
import {
  StartNode,
  NoteNode,
  PromptNode,
  FormNode,
  BranchNodeRenderer,
  EndNode,
  UnknownNode,
} from './nodes';

interface Props {
  currentNode: any;
  input: Record<string, any>;
  onInputChange: (input: Record<string, any>) => void;
  onAdvance: (branchTarget?: string) => void;
  onBack?: () => void;
  isAdvancing: boolean;
  completed: boolean;
  onReset: () => void;
  sessionState?: Record<string, any>;
  sessionId?: string | null;
  mode?: 'preview' | 'live';
}

export default function SimulatorNodeRenderer({
  currentNode,
  input,
  onInputChange,
  onAdvance,
  onBack,
  isAdvancing,
  completed,
  onReset,
  sessionState = {},
  sessionId,
  mode,
}: Props) {
  if (!currentNode) return null;

  // Merge committed session state with the current step's live input so
  // variable bubbles update in real time as the user types.
  const liveVars = { ...sessionState, ...input };

  const renderNodeContent = () => {
    switch (currentNode.type) {
      case 'Start':
        return <StartNode />;

      case 'Note':
        return <NoteNode content={currentNode.content} sessionState={liveVars} />;

      case 'Prompt':
        return (
          <PromptNode
            content={currentNode.content}
            varId={currentNode.varId}
            value={input[currentNode.varId] || ''}
            onChange={(value) => onInputChange({ ...input, [currentNode.varId]: value })}
          />
        );

      case 'Page':
        return (
          <PageNodeRenderer
            title={currentNode.title}
            content={currentNode.content}
            elements={currentNode.elements || []}
            input={input}
            onInputChange={onInputChange}
            onAdvance={onAdvance}
            onBack={onBack}
            isAdvancing={isAdvancing}
            sessionState={liveVars}
            sessionId={sessionId}
            mode={mode}
          />
        );

      case 'ContactUs':
        return (
          <FormNode
            title={currentNode.title}
            description={currentNode.description}
            fields={currentNode.fields || []}
            input={input}
            onInputChange={onInputChange}
            submitLabel={currentNode.submitButtonText}
          />
        );

      case 'Branch':
        return (
          <BranchNodeRenderer
            title={currentNode.title}
            branches={currentNode.branches || []}
            onSelect={(target) => {
              // We pass the target as 'selectedBranch' in input
              onInputChange({ ...input, selectedBranch: target });
              // And immediately advance? Or let user click Next?
              // Usually branch selection acts as "Next".
              // But onInputChange is async state update.
              // We might need a way to trigger advance with input.
              // For now, let's update input and let user click Next, OR we can modify onAdvance to accept input.
              // But SimulatorCurrentNode passes onAdvance which calls mutation.
              // Let's just update input and user clicks Next?
              // Or better: update input AND call onAdvance?
              // But state update might not be flushed.
              // Let's stick to update input, but maybe auto-advance?
              // Actually, for Branch, we probably want buttons that do "Select & Next".
              // But `onInputChange` updates local state `input`.
              // `onAdvance` uses that `input`.
              // So we can do: onInputChange(...); setTimeout(onAdvance, 0);
            }}
            onAdvance={onAdvance}
          />
        );

      case 'End':
        return <EndNode outcome={currentNode.outcome} />;

      default:
        return <UnknownNode type={currentNode.type} />;
    }
  };

  // Pages carry their own controls; End and the completed state are terminal.
  const showControls = !completed && currentNode.type !== 'End' && currentNode.type !== 'Page';
  // A Branch advances by choosing a branch, so it has no Next of its own.
  const showNext = showControls && currentNode.type !== 'Branch';

  return (
    <Box>
      {renderNodeContent()}

      {/* Page nodes render their own Back/Next controls; every other in-progress
          node type gets them here. Branch nodes advance by choosing a branch, so
          they get Back on its own — without it, picking a wrong branch is a dead
          end with no way to reconsider. */}
      {/* Skipped entirely when it would be empty — a Branch on the first step has
          neither a Back to offer nor a Next, and an empty row just adds a gap. */}
      {showControls && (onBack || showNext) && (
        <Group justify="space-between" mt="xl">
          <BackButton onBack={onBack} isAdvancing={isAdvancing} />
          {showNext && (
            <Button
              rightSection={<IconArrowRight size={16} />}
              onClick={() => onAdvance()}
              loading={isAdvancing}
              style={{ backgroundColor: NAVY }}
              radius="md"
              size="md"
            >
              {currentNode.type === 'ContactUs' ? (currentNode.submitButtonText || 'Submit') : 'Next Step'}
            </Button>
          )}
        </Group>
      )}

      {completed && (
        <Button onClick={onReset} variant="outline" color={NAVY} fullWidth mt="lg" radius="md" size="md">
          Reset & Start Over
        </Button>
      )}
    </Box>
  );
}
